import type { ClintplayEvent, PerformanceEventData } from '../types';

type EventCallback = (event: ClintplayEvent) => void;
type Metric = PerformanceEventData['metric'];

const THRESHOLDS: Record<Metric, [number, number]> = {
  LCP: [1000, 4000],
  FID: [100, 300],
  CLS: [0.1, 0.25],
  TTFB: [800, 1800],
  INP: [200, 500],
};

/**
 * The three metrics that are only meaningful ONCE, at the end of the page's
 * life, and why each was wrong before.
 *
 * Every one of these observers fires repeatedly. The previous version emitted a
 * row from inside the callback, so the wire carried the whole intermediate
 * series instead of the answer:
 *
 *   CLS — `clsValue` is a running total, re-emitted on every layout shift. Each
 *         partial sum is SMALLER than the final one, so feeding them all into a
 *         percentile drags it DOWN. A p75 of 0.705 already reading "poor" was
 *         understated by construction.
 *   INP — emitted `entry.duration` per interaction, which is not INP at all.
 *         INP is the WORST interaction on the page (the official definition is
 *         literally the worst one for pages under 50 interactions), reported
 *         once.
 *   LCP — emitted every larger candidate, so a page whose hero image resolves
 *         in three steps wrote three rows.
 *
 * Measured over 7 days on one project: 231.8M INP rows and 41.0M CLS against
 * 1.1M LCP — together 99.2% of `performance_metrics`, which at 292 GB is the
 * largest table in the database.
 *
 * FID and TTFB are NOT in this set: `first-input` and `navigation` each fire
 * once per page by definition, so they still emit inline. Adding them here
 * would risk losing them for no benefit.
 */
type FinalMetric = 'LCP' | 'CLS' | 'INP';

export class PerformanceTracker {
  private observers: PerformanceObserver[] = [];
  private pending = new Map<FinalMetric, number>();
  /**
   * Report-once, and it has to be a guard rather than a listener that removes
   * itself: `visibilitychange` fires again every time the user comes back and
   * leaves, and a second emission would put a partial series back on the wire —
   * the exact defect this class was rewritten to remove.
   */
  private reported = false;

  constructor(
    private sessionId: string,
    private emit: EventCallback
  ) {}

  start(): void {
    if (typeof PerformanceObserver === 'undefined') return;
    this.observeLCP();
    this.observeFID();
    this.observeCLS();
    this.observeTTFB();
    this.observeINP();
  }

  stop(): void {
    // Before disconnecting, not after: a session that ends without the tab ever
    // being hidden (SPA teardown, explicit shutdown) is the one path where no
    // visibility event ever arrives, and dropping the observers first would
    // throw away values that were never sent.
    this.flushFinal();
    this.observers.forEach((o) => o.disconnect());
    this.observers = [];
  }

  /**
   * Emits the final value of each accumulated metric, at most once per page
   * load. Safe to call repeatedly and from more than one trigger.
   *
   * ORDERING, which is load-bearing and not obvious: `Transport` flushes its
   * buffer on `visibilitychange`, and DOM listeners run in registration order.
   * `transport.start()` runs at index.ts before the tracker exists, so a
   * listener registered by THIS class would run second — emitting into a buffer
   * that was just drained, on the one occasion (page closing) when there is no
   * later flush to catch it. index.ts therefore registers the hide handler
   * BEFORE `transport.start()`, and `tests/modules/performance-tracker.test.ts`
   * asserts the payload actually leaves, not merely that emit was called.
   */
  flushFinal(): void {
    if (this.reported) return;
    this.reported = true;
    for (const [metric, value] of this.pending) this.handleMetric(metric, value);
    this.pending.clear();
  }

  handleMetric(metric: Metric, value: number): void {
    const [good, poor] = THRESHOLDS[metric];
    const rating: PerformanceEventData['rating'] =
      value <= good ? 'good' : value <= poor ? 'needs-improvement' : 'poor';
    this.emit({
      type: 'performance',
      timestamp: Date.now(),
      sessionId: this.sessionId,
      data: { metric, value, rating },
    });
  }

  /**
   * LCP and CLS only ever grow, so `max` is their final value. INP wants the
   * worst interaction, which is also `max`. One rule, and it is the definition
   * rather than an approximation of it.
   */
  private record(metric: FinalMetric, value: number): void {
    const prev = this.pending.get(metric);
    if (prev === undefined || value > prev) this.pending.set(metric, value);
  }

  private observeLCP(): void {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const last = entries[entries.length - 1];
        if (last) this.record('LCP', last.startTime);
      });
      observer.observe({ type: 'largest-contentful-paint', buffered: true });
      this.observers.push(observer);
    } catch {}
  }

  private observeFID(): void {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const first = entries[0] as any;
        if (first) this.handleMetric('FID', first.processingStart - first.startTime);
      });
      observer.observe({ type: 'first-input', buffered: true });
      this.observers.push(observer);
    } catch {}
  }

  private observeCLS(): void {
    let clsValue = 0;
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries() as any[]) {
          if (!entry.hadRecentInput) clsValue += entry.value;
        }
        this.record('CLS', clsValue);
      });
      observer.observe({ type: 'layout-shift', buffered: true });
      this.observers.push(observer);
    } catch {}
  }

  private observeTTFB(): void {
    try {
      const observer = new PerformanceObserver((list) => {
        const entry = list.getEntries()[0] as any;
        if (entry) this.handleMetric('TTFB', entry.responseStart);
      });
      observer.observe({ type: 'navigation', buffered: true });
      this.observers.push(observer);
    } catch {}
  }

  private observeINP(): void {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries() as any[]) {
          this.record('INP', entry.duration);
        }
      });
      observer.observe({ type: 'event', buffered: true });
      this.observers.push(observer);
    } catch {}
  }
}
